
import { useState } from 'react';
import type { Vehicle } from '../page';

interface FilterSidebarProps {
  filters: {
    priceRange: number[];
    mileageRange: number[];
    yearRange: number[];
    makes: string[];
    models: string[];
    bodyTypes: string[];
    transmissions: string[];
    fuelTypes: string[];
    colors: string[];
  };
  setFilters: React.Dispatch<React.SetStateAction<any>>;
  vehicles: Vehicle[];
  resetFilters: () => void;
}

export default function FilterSidebar({ filters, setFilters, vehicles, resetFilters }: FilterSidebarProps) {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  // Local pending state — staged until Apply is clicked
  const [pending, setPending] = useState({ ...filters });

  const toggleSection = (section: string) => {
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      // Sync pending with current applied filters when opening
      setPending({ ...filters });
      setExpandedSection(section);
    }
  };

  const applySection = () => {
    setFilters({ ...pending });
    setExpandedSection(null);
  };

  const uniqueMakes = Array.from(new Set(vehicles.map(v => v.make))).sort();
  const uniqueModels = Array.from(new Set(vehicles.map(v => v.model))).sort();
  const uniqueBodyTypes = Array.from(new Set(vehicles.map(v => v.bodyType))).sort();
  const uniqueTransmissions = Array.from(new Set(vehicles.map(v => v.transmission))).sort();
  const uniqueFuelTypes = Array.from(new Set(vehicles.map(v => v.fuelType))).sort();
  const uniqueColors = Array.from(new Set(vehicles.map(v => v.color))).sort();

  const handleCheckboxChange = (category: string, value: string) => {
    setPending((prev: any) => {
      const currentValues = prev[category] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v: string) => v !== value)
        : [...currentValues, value];
      return { ...prev, [category]: newValues };
    });
  };

  const activeFilterCount =
    (filters.makes.length > 0 ? 1 : 0) +
    (filters.models.length > 0 ? 1 : 0) +
    (filters.bodyTypes.length > 0 ? 1 : 0) +
    (filters.transmissions.length > 0 ? 1 : 0) +
    (filters.fuelTypes.length > 0 ? 1 : 0) +
    (filters.colors.length > 0 ? 1 : 0);

  const SectionHeader = ({ id, label, badge }: { id: string; label: string; badge?: string }) => (
    <button
      onClick={() => toggleSection(id)}
      className="w-full flex items-center justify-between mb-3 cursor-pointer"
    >
      <h3 className="font-semibold text-gray-900 text-sm sm:text-base flex items-center gap-1">
        {label}
        {badge && (
          <span className="ml-1 text-xs text-[#E85D26] font-normal">{badge}</span>
        )}
      </h3>
      <i className={`ri-arrow-${expandedSection === id ? 'up' : 'down'}-s-line text-gray-600`}></i>
    </button>
  );

  const ApplyButton = () => (
    <button
      onClick={applySection}
      className="mt-4 w-full py-2 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
    >
      Apply
    </button>
  );

  return (
    <div className="w-full mb-6">
      <div className="bg-[#F8F9FA] rounded-2xl p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">Filters</h2>
          {activeFilterCount > 0 && (
            <button
              onClick={resetFilters}
              className="text-sm text-[#E85D26] hover:underline cursor-pointer whitespace-nowrap"
            >
              Clear All ({activeFilterCount})
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">

          {/* Price Range */}
          <div className="border-b border-gray-200 pb-4">
            <SectionHeader
              id="price"
              label="Price Range"
              badge={
                filters.priceRange[0] > 0 || filters.priceRange[1] < 50000
                  ? `($${filters.priceRange[0].toLocaleString()} – $${filters.priceRange[1].toLocaleString()})`
                  : undefined
              }
            />
            {expandedSection === 'price' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span>${pending.priceRange[0].toLocaleString()}</span>
                  <span>${pending.priceRange[1].toLocaleString()}</span>
                </div>
                <input type="range" min="0" max="50000" step="1000"
                  value={pending.priceRange[0]}
                  onChange={(e) => setPending((prev: any) => ({ ...prev, priceRange: [parseInt(e.target.value), prev.priceRange[1]] }))}
                  className="w-full cursor-pointer"
                />
                <input type="range" min="0" max="50000" step="1000"
                  value={pending.priceRange[1]}
                  onChange={(e) => setPending((prev: any) => ({ ...prev, priceRange: [prev.priceRange[0], parseInt(e.target.value)] }))}
                  className="w-full cursor-pointer"
                />
                <ApplyButton />
              </div>
            )}
          </div>

          {/* Mileage Range */}
          <div className="border-b border-gray-200 pb-4">
            <SectionHeader
              id="mileage"
              label="Mileage"
              badge={
                filters.mileageRange[0] > 0 || filters.mileageRange[1] < 150000
                  ? `(${filters.mileageRange[0].toLocaleString()} – ${filters.mileageRange[1].toLocaleString()} mi)`
                  : undefined
              }
            />
            {expandedSection === 'mileage' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span>{pending.mileageRange[0].toLocaleString()} mi</span>
                  <span>{pending.mileageRange[1].toLocaleString()} mi</span>
                </div>
                <input type="range" min="0" max="150000" step="5000"
                  value={pending.mileageRange[0]}
                  onChange={(e) => setPending((prev: any) => ({ ...prev, mileageRange: [parseInt(e.target.value), prev.mileageRange[1]] }))}
                  className="w-full cursor-pointer"
                />
                <input type="range" min="0" max="150000" step="5000"
                  value={pending.mileageRange[1]}
                  onChange={(e) => setPending((prev: any) => ({ ...prev, mileageRange: [prev.mileageRange[0], parseInt(e.target.value)] }))}
                  className="w-full cursor-pointer"
                />
                <ApplyButton />
              </div>
            )}
          </div>

          {/* Year Range */}
          <div className="border-b border-gray-200 pb-4">
            <SectionHeader
              id="year"
              label="Year"
              badge={
                filters.yearRange[0] > 2015 || filters.yearRange[1] < 2024
                  ? `(${filters.yearRange[0]} – ${filters.yearRange[1]})`
                  : undefined
              }
            />
            {expandedSection === 'year' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span>{pending.yearRange[0]}</span>
                  <span>{pending.yearRange[1]}</span>
                </div>
                <input type="range" min="2015" max="2024" step="1"
                  value={pending.yearRange[0]}
                  onChange={(e) => setPending((prev: any) => ({ ...prev, yearRange: [parseInt(e.target.value), prev.yearRange[1]] }))}
                  className="w-full cursor-pointer"
                />
                <input type="range" min="2015" max="2024" step="1"
                  value={pending.yearRange[1]}
                  onChange={(e) => setPending((prev: any) => ({ ...prev, yearRange: [prev.yearRange[0], parseInt(e.target.value)] }))}
                  className="w-full cursor-pointer"
                />
                <ApplyButton />
              </div>
            )}
          </div>

          {/* Make */}
          <div className="border-b border-gray-200 pb-4">
            <SectionHeader
              id="make"
              label="Make"
              badge={filters.makes.length > 0 ? `(${filters.makes.length} selected)` : undefined}
            />
            {expandedSection === 'make' && (
              <div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {uniqueMakes.map(make => (
                    <label key={make} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox"
                        checked={pending.makes.includes(make)}
                        onChange={() => handleCheckboxChange('makes', make)}
                        className="w-4 h-4 rounded cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">{make}</span>
                    </label>
                  ))}
                </div>
                <ApplyButton />
              </div>
            )}
          </div>

          {/* Model */}
          <div className="border-b border-gray-200 pb-4">
            <SectionHeader
              id="model"
              label="Model"
              badge={filters.models.length > 0 ? `(${filters.models.length} selected)` : undefined}
            />
            {expandedSection === 'model' && (
              <div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {uniqueModels.map(model => (
                    <label key={model} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox"
                        checked={pending.models.includes(model)}
                        onChange={() => handleCheckboxChange('models', model)}
                        className="w-4 h-4 rounded cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">{model}</span>
                    </label>
                  ))}
                </div>
                <ApplyButton />
              </div>
            )}
          </div>

          {/* Body Type */}
          <div className="border-b border-gray-200 pb-4">
            <SectionHeader
              id="bodyType"
              label="Body Type"
              badge={filters.bodyTypes.length > 0 ? `(${filters.bodyTypes.length} selected)` : undefined}
            />
            {expandedSection === 'bodyType' && (
              <div>
                <div className="space-y-2">
                  {uniqueBodyTypes.map(type => (
                    <label key={type} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox"
                        checked={pending.bodyTypes.includes(type)}
                        onChange={() => handleCheckboxChange('bodyTypes', type)}
                        className="w-4 h-4 rounded cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">{type}</span>
                    </label>
                  ))}
                </div>
                <ApplyButton />
              </div>
            )}
          </div>

          {/* Transmission */}
          <div className="border-b border-gray-200 pb-4">
            <SectionHeader
              id="transmission"
              label="Transmission"
              badge={filters.transmissions.length > 0 ? `(${filters.transmissions.length} selected)` : undefined}
            />
            {expandedSection === 'transmission' && (
              <div>
                <div className="space-y-2">
                  {uniqueTransmissions.map(trans => (
                    <label key={trans} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox"
                        checked={pending.transmissions.includes(trans)}
                        onChange={() => handleCheckboxChange('transmissions', trans)}
                        className="w-4 h-4 rounded cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">{trans}</span>
                    </label>
                  ))}
                </div>
                <ApplyButton />
              </div>
            )}
          </div>

          {/* Fuel Type */}
          <div className="border-b border-gray-200 pb-4">
            <SectionHeader
              id="fuelType"
              label="Fuel Type"
              badge={filters.fuelTypes.length > 0 ? `(${filters.fuelTypes.length} selected)` : undefined}
            />
            {expandedSection === 'fuelType' && (
              <div>
                <div className="space-y-2">
                  {uniqueFuelTypes.map(fuel => (
                    <label key={fuel} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox"
                        checked={pending.fuelTypes.includes(fuel)}
                        onChange={() => handleCheckboxChange('fuelTypes', fuel)}
                        className="w-4 h-4 rounded cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">{fuel}</span>
                    </label>
                  ))}
                </div>
                <ApplyButton />
              </div>
            )}
          </div>

          {/* Color */}
          <div className="pb-4">
            <SectionHeader
              id="color"
              label="Color"
              badge={filters.colors.length > 0 ? `(${filters.colors.length} selected)` : undefined}
            />
            {expandedSection === 'color' && (
              <div>
                <div className="space-y-2">
                  {uniqueColors.map(color => (
                    <label key={color} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox"
                        checked={pending.colors.includes(color)}
                        onChange={() => handleCheckboxChange('colors', color)}
                        className="w-4 h-4 rounded cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">{color}</span>
                    </label>
                  ))}
                </div>
                <ApplyButton />
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
